import { supabase } from "../../lib/supabase";
import { DOCUMENT_PROFILE, compressImage } from "../../lib/images";
import type { MotStatus, ServiceStatus } from "../../lib/vehicleStatus";

/* ---------------------------------------------------------------- types */

export interface FleetVehicle {
  id: number;
  registration: string;
  colloquial_name: string | null;
  make: string;
  model: string;
  year: number | null;
  colour: string | null;
  fuel_type: string | null;
  vehicle_type: string | null;
  usage_type: "primary_driver" | "pool_van";
  availability_state: "available" | "booked" | "unavailable" | "off_road";
  is_active: boolean;
  has_tracker: boolean;
  current_mileage: number | null;
  current_mileage_updated_at: string | null;
  notes: string | null;
  primary_user_id: string | null;
  primary_user_name: string | null;
  /** Who the vehicle belongs to on the roster, signed in or not. */
  primary_person_id: number | null;
  /** Assigned, but that person has no app account yet. */
  driver_no_account: boolean;
  mot_expiry_date: string | null;
  mot_last_checked_at: string | null;
  mot_check_status: string | null;
  mot_last_test_result: string | null;
  mot_has_outstanding_recall: string | null;
  mot_plate_mismatch: boolean | null;
  mot_status: MotStatus;
  mot_days_remaining: number | null;
  last_service_date: string | null;
  next_service_date: string | null;
  next_service_mileage: number | null;
  service_interval_miles: number | null;
  service_interval_months: number | null;
  service_status: ServiceStatus;
  ram_last_seen: string | null;
  ram_last_location: string | null;
  open_issue_count: number;
  current_booking_id: number | null;
  current_booking_ref: string | null;
  current_booking_driver: string | null;
  current_booking_end: string | null;
}

export interface DashboardStats {
  total: number;
  active: number;
  pool_vans: number;
  mot_expired: number;
  mot_due_soon: number;
  service_overdue: number;
  service_due_soon: number;
  off_road: number;
  open_issues: number;
  untracked: number;
}

export interface MotTest {
  id: number;
  mot_test_number: string;
  completed_date: string | null;
  test_result: string | null;
  expiry_date: string | null;
  odometer_value: number | null;
  odometer_unit: string | null;
  is_synthetic_number: boolean;
  defects: unknown;
}

export interface ServiceRecord {
  id: number;
  service_date: string;
  service_type: string | null;
  mileage: number | null;
  supplier: string | null;
  cost: string | number | null;
  advisories: string | null;
  advisories_actioned: boolean;
  notes: string | null;
  recorded_by_name: string | null;
}

export interface Unavailability {
  id: number;
  start_date: string;
  end_date: string;
  reason: string | null;
}

export interface VehicleIssue {
  id: number;
  vehicle_id: number;
  registration: string;
  colloquial_name: string | null;
  make: string;
  model: string;
  severity: string | null;
  description: string;
  status: string;
  reported_at: string;
  reported_by: string | null;
  reported_by_name: string | null;
  resolved_at: string | null;
  resolved_by_name: string | null;
  resolution: string | null;
}

export interface Booking {
  id: number;
  booking_ref: string;
  vehicle_id: number;
  registration: string;
  colloquial_name: string | null;
  make: string;
  model: string;
  driver_id: string | null;
  driver_name: string | null;
  start_at: string;
  end_at: string;
  purpose: string | null;
  destination: string | null;
  status: string;
  cancelled_at: string | null;
  cancelled_reason: string | null;
  is_mine: boolean;
}

export interface TelemetryPoint {
  captured_at: string;
  lat: number | null;
  lng: number | null;
  location_text: string | null;
  odometer: number | null;
  odometer_unit: string | null;
  driver_name: string | null;
}

/** One row per vehicle, latest known fix only. See v_vehicle_positions. */
export interface VehiclePosition {
  vehicle_id: number;
  captured_at: string;
  lat: number;
  lng: number;
  location_text: string | null;
  odometer: number | null;
  odometer_unit: string | null;
  driver_name: string | null;
}

export interface VehicleDetail {
  vehicle: FleetVehicle | null;
  mot_tests: MotTest[];
  service_records: ServiceRecord[];
  unavailability: Unavailability[];
  issues: VehicleIssue[];
  bookings: Booking[];
  /** Empty unless the caller holds can_view_vehicle_telemetry. RLS decides. */
  telemetry: TelemetryPoint[];
}

export interface CalendarVan {
  vehicle_id: number;
  registration: string;
  colloquial_name: string | null;
  make: string;
  model: string;
  availability_state: string;
  bookings: {
    id: number;
    booking_ref: string;
    driver_id: string;
    driver_name: string | null;
    start_at: string;
    end_at: string;
    purpose: string | null;
    destination: string | null;
    is_mine: boolean;
  }[];
  unavailable: { id: number; start_date: string; end_date: string; reason: string | null }[];
}

/* -------------------------------------- vehicle compliance tests (0194) */

export type CheckStatus = "never" | "overdue" | "due" | "ok";

/** One fortnightly vehicle check. Nil-defect returns are recorded
 * too: proving the test happened is the point, not only logging faults. */
export interface VehicleCheck {
  id: number;
  vehicle_id: number;
  checked_by: string | null;
  checked_at: string;
  odometer_miles: number | null;
  lights_ok: boolean | null;
  oil_ok: boolean | null;
  water_ok: boolean | null;
  electrics_ok: boolean | null;
  tyres_ok: boolean | null;
  bodywork_ok: boolean | null;
  driver_fit: boolean;
  defects_found: boolean;
  defect_notes: string | null;
  signed_name: string | null;
  signed_at: string | null;
}

/** What the driver ticked. Null means "not applicable" (an EV has no oil
 * to check) which is deliberately distinct from false, "checked and
 * faulty" — the backend only raises a defect for an explicit false. */
export interface VehicleCheckSubmission {
  vehicle_id: number;
  odometer_miles: number | null;
  lights_ok?: boolean | null;
  oil_ok?: boolean | null;
  water_ok?: boolean | null;
  electrics_ok?: boolean | null;
  tyres_ok?: boolean | null;
  bodywork_ok?: boolean | null;
  driver_fit: boolean;
  defect_notes?: string | null;
  signed_name: string;
}

export interface FleetCompliance {
  vehicle_id: number;
  registration: string;
  colloquial_name: string | null;
  make: string | null;
  model: string | null;
  primary_user_id: string | null;
  primary_user_name: string | null;
  usage_type: "primary_driver" | "pool_van";
  check_status: CheckStatus;
  last_checked_at: string | null;
  estimated_miles: number | null;
  mileage_as_at: string | null;
  mileage_source: "driver" | "mot" | "service" | "import" | "correction" | null;
  /** True when the figure spans a period the tracker was not recording, so
   * it is a floor rather than a current odometer. */
  mileage_is_minimum: boolean;
  mileage_gap_days: number | null;
  tracker_delta_implausible: boolean;
  open_issue_count: number;
  mot_expiry_date: string | null;
  mot_days_remaining: number | null;
  next_service_date: string | null;
  service_days_remaining: number | null;
  next_service_mileage: number | null;
  service_miles_remaining: number | null;
  missing: string[];
}

/* --------------------------------------------------------------- reads */

/** Whole fleet list in one request. */
export async function fetchFleet(): Promise<FleetVehicle[]> {
  const { data, error } = await supabase
    .from("v_vehicle_fleet")
    .select("*")
    .order("registration");
  if (error) throw error;
  return (data ?? []) as FleetVehicle[];
}

/** Every headline figure in one request. */
export async function fetchDashboard(): Promise<DashboardStats> {
  const { data, error } = await supabase.rpc("vehicle_dashboard");
  if (error) throw error;
  return data as DashboardStats;
}

/**
 * Latest position per vehicle. Comes back empty for anyone without
 * can_view_vehicle_telemetry — RLS on vehicle_telemetry_snapshots decides,
 * not this call. Callers should degrade gracefully (show the grouped
 * vehicle list without markers) rather than treat an empty result as an
 * error.
 */
export async function fetchVehiclePositions(): Promise<VehiclePosition[]> {
  const { data, error } = await supabase.from("v_vehicle_positions").select("*");
  if (error) throw error;
  return (data ?? []) as VehiclePosition[];
}

/** Whole detail page in one request. */
export async function fetchVehicleDetail(id: number): Promise<VehicleDetail> {
  const { data, error } = await supabase.rpc("vehicle_detail", { p_id: id });
  if (error) throw error;
  return data as VehicleDetail;
}

export async function fetchIssues(status?: string): Promise<VehicleIssue[]> {
  let q = supabase.from("v_vehicle_issue_list").select("*").order("reported_at", {
    ascending: false,
  });
  if (status && status !== "all") q = q.eq("status", status);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as VehicleIssue[];
}

export interface InsurancePolicy {
  id: number;
  policy_type: string;
  insurer: string | null;
  policy_number: string | null;
  broker: string | null;
  renewal_date: string | null;
}

/**
 * Read access to insurance_policies is a staff baseline (app.is_staff()),
 * not gated by a permission — every signed-in, active user can see this,
 * same as the legacy Glide /insurance screen. "Motor" is free-text
 * (policy_type has no fixed vocabulary), hence the ilike rather than an
 * exact match.
 */
export async function fetchMotorInsurancePolicies(): Promise<InsurancePolicy[]> {
  const { data, error } = await supabase
    .from("insurance_policies")
    .select("id,policy_type,insurer,policy_number,broker,renewal_date")
    .eq("is_active", true)
    .ilike("policy_type", "%motor%")
    .order("renewal_date");
  if (error) throw error;
  return (data ?? []) as InsurancePolicy[];
}

export async function fetchBookings(mineOnly: boolean): Promise<Booking[]> {
  let q = supabase
    .from("v_pool_booking_list")
    .select("*")
    .order("start_at", { ascending: false })
    .limit(200);
  if (mineOnly) q = q.eq("is_mine", true);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as Booking[];
}

/** The whole week grid — every van, its bookings and blocks — in one request. */
export async function fetchPoolCalendar(from: string, to: string): Promise<CalendarVan[]> {
  const { data, error } = await supabase.rpc("pool_calendar", { p_from: from, p_to: to });
  if (error) throw error;
  return (data ?? []) as CalendarVan[];
}

/* -------------------------------------------------------------- writes */

export type BookResult =
  | { ok: true; id: number; booking_ref: string }
  | { ok: false; error: string; booking_ref?: string; driver_name?: string };

/** Conflict check and insert happen together, server-side. */
export async function bookPoolVan(input: {
  vehicleId: number;
  startAt: string;
  endAt: string;
  purpose?: string;
  destination?: string;
}): Promise<BookResult> {
  const { data, error } = await supabase.rpc("book_pool_van", {
    p_vehicle_id: input.vehicleId,
    p_start_at: new Date(input.startAt).toISOString(),
    p_end_at: new Date(input.endAt).toISOString(),
    p_purpose: input.purpose ?? null,
    p_destination: input.destination ?? null,
  });
  if (error) throw error;
  return data as BookResult;
}

export async function cancelBooking(id: number, reason: string): Promise<void> {
  const { error } = await supabase
    .from("pool_bookings")
    .update({ status: "cancelled", cancelled_at: new Date().toISOString(), cancelled_reason: reason })
    .eq("id", id);
  if (error) throw error;
}

export async function reportIssue(input: {
  vehicleId: number;
  severity: string;
  description: string;
  reportedBy: string;
}): Promise<void> {
  const { error } = await supabase.from("vehicle_issues").insert({
    vehicle_id: input.vehicleId,
    severity: input.severity,
    description: input.description,
    reported_by: input.reportedBy,
    status: "open",
  });
  if (error) throw error;
}

export async function resolveIssue(id: number, resolution: string, by: string): Promise<void> {
  const { error } = await supabase
    .from("vehicle_issues")
    .update({
      status: "resolved",
      resolution,
      resolved_by: by,
      resolved_at: new Date().toISOString(),
    })
    .eq("id", id);
  if (error) throw error;
}

/**
 * Ticks off the advisories on one service record.
 *
 * The Outstanding/Actioned badge on the service history has been rendered
 * since the module was built and nothing could ever change it, so every
 * service with advisories read "Outstanding" for ever -- a light that
 * only ever shows red tells you nothing.
 *
 * Reversible on purpose. "Actioned" is somebody's judgement that the
 * garage's recommendations have been dealt with, and a judgement made in
 * error has to be takeable back; there is no audit weight on this flag
 * that untricking it would undermine.
 */
export async function setAdvisoriesActioned(
  serviceRecordId: number,
  actioned: boolean,
  by: string,
): Promise<void> {
  const { error } = await supabase
    .from("vehicle_service_records")
    .update({
      advisories_actioned: actioned,
      advisories_actioned_by: actioned ? by : null,
    })
    .eq("id", serviceRecordId);
  if (error) throw error;
}

export async function updateMileage(vehicleId: number, mileage: number): Promise<void> {
  const { error } = await supabase
    .from("vehicles")
    .update({ current_mileage: mileage, current_mileage_updated_at: new Date().toISOString() })
    .eq("id", vehicleId);
  if (error) throw error;
}

export async function updateVehicle(
  id: number,
  patch: Partial<
    Pick<
      FleetVehicle,
      | "colloquial_name"
      | "notes"
      | "availability_state"
      | "usage_type"
      | "has_tracker"
      | "service_interval_miles"
      | "service_interval_months"
      | "next_service_date"
    >
  >,
): Promise<void> {
  const { error } = await supabase.from("vehicles").update(patch).eq("id", id);
  if (error) throw error;
}

/**
 * Records a service. The document is REQUIRED and is uploaded first, so
 * a failed upload never leaves a record with nothing behind it.
 *
 * The database says the same thing (0241), so this is the courtesy and
 * that is the guarantee: a service nobody can produce a document for is
 * a claim, not a record.
 */
export async function addServiceRecord(input: {
  vehicleId: number;
  serviceDate: string;
  serviceType: string;
  mileage: number | null;
  supplier: string;
  cost: number | null;
  advisories: string;
  recordedBy: string;
  document: File;
}): Promise<void> {
  const { fileId } = await uploadVehicleDocument(
    input.vehicleId,
    input.document,
    input.recordedBy,
    "service_record",
  );

  const { error } = await supabase.from("vehicle_service_records").insert({
    vehicle_id: input.vehicleId,
    service_date: input.serviceDate,
    service_type: input.serviceType || null,
    mileage: input.mileage,
    supplier: input.supplier || null,
    cost: input.cost,
    advisories: input.advisories || null,
    invoice_file_id: fileId,
    recorded_by: input.recordedBy,
  });
  if (error) throw error;
}

export async function addUnavailability(input: {
  vehicleId: number;
  startDate: string;
  endDate: string;
  reason: string;
  createdBy: string;
}): Promise<void> {
  const { error } = await supabase.from("vehicle_unavailability").insert({
    vehicle_id: input.vehicleId,
    start_date: input.startDate,
    end_date: input.endDate,
    reason: input.reason || null,
    created_by: input.createdBy,
  });
  if (error) throw error;
}

export async function deleteUnavailability(id: number): Promise<void> {
  const { error } = await supabase.from("vehicle_unavailability").delete().eq("id", id);
  if (error) throw error;
}

/* ------------------------------------------------- removing a vehicle */

export interface DeleteImpact {
  vehicle: {
    id: number;
    registration: string;
    colloquial_name: string | null;
    is_active: boolean;
    is_test_fixture: boolean;
  } | null;
  mot_tests: number;
  telemetry: number;
  service_records: number;
  bookings: number;
  issues: number;
  unavailability: number;
  files: number;
  can_archive: boolean;
  can_purge: boolean;
}

export type DeleteResult =
  | { ok: true; mode: "archive"; id: number; registration: string }
  | {
      ok: true;
      mode: "purge";
      id: number;
      registration: string;
      removed: Record<string, number>;
    }
  | { ok: false; error: string; detail?: string };

/** What would be destroyed, and whether this user may do it. */
export async function fetchDeleteImpact(id: number): Promise<DeleteImpact> {
  const { data, error } = await supabase.rpc("vehicle_delete_impact", { p_id: id });
  if (error) throw error;
  return data as DeleteImpact;
}

/**
 * `archive` keeps the history and is reversible. `purge` is permanent and
 * takes the MOT and service records with it. The database enforces the
 * permission either way — this is not a client-side decision.
 */
export async function deleteVehicle(
  id: number,
  mode: "archive" | "purge",
): Promise<DeleteResult> {
  const { data, error } = await supabase.rpc("delete_vehicle", { p_id: id, p_mode: mode });
  if (error) throw error;
  return data as DeleteResult;
}

export async function restoreVehicle(id: number): Promise<void> {
  const { error } = await supabase.from("vehicles").update({ is_active: true }).eq("id", id);
  if (error) throw error;
}

/* --------------------------------------------- integration refresh */

/**
 * The sync edge functions serve both the nightly cron and these manual
 * buttons through one entry point, so a check behaves identically
 * whoever asked for it.
 *
 * The browser never calls the function directly: it asks the database
 * (request_manual_sync) and Postgres relays server-side via pg_net —
 * the same transport the scheduler uses. Everything therefore travels
 * over /rest/v1, which sidesteps managed-device web filters that were
 * observed silently killing browser POSTs to /functions/v1.
 * Authorisation stays in the database: the RPC requires
 * `can_manage_vehicles`, and the function verifies the relay secret.
 */
interface SyncOutcome {
  done: boolean;
  status?: number;
  body?: {
    ok?: boolean;
    reason?: string;
    error?: string;
    results?: Array<{ status: string; error?: string }>;
  };
}

const SYNC_POLL_MS = 1500;
const SYNC_POLL_LIMIT = 40; // ≤ 60 s; a single-vehicle sync takes 2–5 s.

async function invokeSync(
  fn: "dvsa-mot-sync" | "ram-tracking-sync",
  vehicleId: number,
): Promise<void> {
  const { data: requestId, error } = await supabase.rpc("request_manual_sync", {
    p_function: fn,
    p_body: { vehicle_id: vehicleId },
  });
  if (error) throw new Error(error.message);

  // pg_net is asynchronous: poll for the function's answer.
  let outcome: SyncOutcome = { done: false };
  for (let i = 0; i < SYNC_POLL_LIMIT && !outcome.done; i++) {
    await new Promise((r) => setTimeout(r, SYNC_POLL_MS));
    const { data, error: pollError } = await supabase.rpc("manual_sync_result", {
      p_request_id: requestId,
    });
    if (pollError) throw new Error(pollError.message);
    outcome = (data ?? { done: false }) as SyncOutcome;
  }
  if (!outcome.done) {
    throw new Error("The sync is taking longer than expected — refresh in a minute to see the result.");
  }

  const body = outcome.body ?? {};
  if (body.error === "no_tracker") throw new Error("This vehicle has no RAM tracker linked.");
  if (body.error) throw new Error(body.error);
  if (outcome.status !== undefined && outcome.status >= 400) {
    throw new Error(`Sync failed (${outcome.status}).`);
  }
  // A 200 can still be a clean skip (credentials not seeded) or a
  // recorded failure (DVSA not_found) — say so instead of silently
  // refreshing to the same screen.
  if (body.ok === false && body.reason) throw new Error(body.reason);
  const first = body.results?.[0];
  if (first && first.status !== "ok") {
    throw new Error(
      first.status === "not_found"
        ? "DVSA has no record of this registration."
        : (first.error ?? `Check failed (${first.status}).`),
    );
  }
}

/** Check one vehicle against the DVSA MOT History API right now. */
export function refreshMot(vehicleId: number): Promise<void> {
  return invokeSync("dvsa-mot-sync", vehicleId);
}

/** Pull one vehicle's location + odometer from RAM Tracking right now. */
export function refreshTracking(vehicleId: number): Promise<void> {
  return invokeSync("ram-tracking-sync", vehicleId);
}

export interface FleetSyncSummary {
  updated: number;
  skipped: number;
  errors: number;
}

interface FleetSyncBody {
  ok?: boolean;
  reason?: string;
  error?: string;
  snapshotCount?: number;
  skippedCount?: number;
  errors?: Array<{ registration: string; error: string }>;
}

const FLEET_SYNC_POLL_LIMIT = 80; // ≤ 120 s: one fleet-list + one odometer
// call plus a reverse-geocode per distinct location, ~1.1 s apart
// (Nominatim's fair-use limit) — see ram-tracking-sync's whole-fleet path.

/**
 * Pull every tracked vehicle's location + odometer from RAM Tracking in
 * one run. request_manual_sync's p_body defaults to '{}', which
 * ram-tracking-sync reads as "no vehicle_id: whole-fleet run" — it calls
 * RAM's fleet-list and fleet-odometer endpoints once each rather than
 * once per vehicle, so this stays inside RAM's 3-requests-per-minute-
 * per-endpoint cap regardless of fleet size.
 */
export async function refreshAllTracking(): Promise<FleetSyncSummary> {
  const { data: requestId, error } = await supabase.rpc("request_manual_sync", {
    p_function: "ram-tracking-sync",
  });
  if (error) throw new Error(error.message);

  let outcome: SyncOutcome & { body?: FleetSyncBody } = { done: false };
  for (let i = 0; i < FLEET_SYNC_POLL_LIMIT && !outcome.done; i++) {
    await new Promise((r) => setTimeout(r, SYNC_POLL_MS));
    const { data, error: pollError } = await supabase.rpc("manual_sync_result", {
      p_request_id: requestId,
    });
    if (pollError) throw new Error(pollError.message);
    outcome = (data ?? { done: false }) as typeof outcome;
  }
  if (!outcome.done) {
    throw new Error("The sync is taking longer than expected — refresh in a minute to see the result.");
  }

  const body = outcome.body ?? {};
  if (body.error) throw new Error(body.error);
  if (outcome.status !== undefined && outcome.status >= 400) {
    throw new Error(`Fleet sync failed (${outcome.status}).`);
  }
  // Credentials not seeded is a clean, expected skip (ok defaults true in
  // that branch's response), not an error — reason is only ever set when
  // ok is explicitly false.
  if (body.ok === false && body.reason) throw new Error(body.reason);
  return {
    updated: body.snapshotCount ?? 0,
    skipped: body.skippedCount ?? 0,
    errors: body.errors?.length ?? 0,
  };
}

/* -------------------------------------- vehicle compliance tests (0194) */

/** Fleet compliance in one request: check state, trusted mileage, MOT and
 * service countdowns, and what data is missing per vehicle. */
export async function fetchFleetCompliance(): Promise<FleetCompliance[]> {
  const { data, error } = await supabase
    .from("v_fleet_compliance")
    .select("*")
    .order("registration");
  if (error) throw error;
  return (data ?? []) as FleetCompliance[];
}

/** The check log for one vehicle, newest first. This is the record that
 * matters in an investigation, so it is never trimmed to "recent". */
export async function fetchVehicleChecks(vehicleId: number): Promise<VehicleCheck[]> {
  const { data, error } = await supabase
    .from("vehicle_checks")
    .select("*")
    .eq("vehicle_id", vehicleId)
    .order("checked_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as VehicleCheck[];
}

/**
 * Submit a compliance test. Goes through the RPC rather than a direct insert
 * because the table's write policy is can_manage_vehicles, which a driver
 * does not have — the RPC's own gate is narrower, allowing a driver to
 * submit only for their own vehicle or a pool vehicle.
 *
 * The RPC also raises a vehicle_issue when a defect is reported, so the
 * caller does not have to remember to.
 */
export async function submitVehicleCheck(input: VehicleCheckSubmission): Promise<number> {
  const { data, error } = await supabase.rpc("vehicle_submit_check", {
    p_vehicle_id: input.vehicle_id,
    p_odometer_miles: input.odometer_miles,
    p_lights_ok: input.lights_ok ?? null,
    p_oil_ok: input.oil_ok ?? null,
    p_water_ok: input.water_ok ?? null,
    p_electrics_ok: input.electrics_ok ?? null,
    p_tyres_ok: input.tyres_ok ?? null,
    p_bodywork_ok: input.bodywork_ok ?? null,
    p_driver_fit: input.driver_fit,
    p_defect_notes: input.defect_notes ?? null,
    p_signed_name: input.signed_name,
  });
  if (error) throw new Error(error.message);
  return data as number;
}

/** One home-screen prompt row. `reason` is the single most pressing thing
 * wrong with the vehicle, named server-side — a prompt that says only
 * "needs attention" makes the reader open it to find out if it matters. */
export interface VehiclePrompt {
  vehicle_id: number;
  registration: string;
  colloquial_name: string | null;
  usage_type: "primary_driver" | "pool_van";
  is_pool: boolean;
  check_status: CheckStatus;
  last_checked_at: string | null;
  open_issue_count: number;
  mot_days_remaining: number | null;
  service_days_remaining: number | null;
  reason: string;
}

/**
 * What this person should be prompted about on app load.
 *
 * The rule lives in the database, not here: your own vehicle when its
 * check is outstanding, plus every pool vehicle with anything outstanding
 * if you are a director or hold can_manage_vehicles. Pool vehicles have no
 * assigned driver, so without that nobody is prompted and nobody feels
 * responsible.
 */
export async function fetchMyVehiclePrompts(): Promise<VehiclePrompt[]> {
  const { data, error } = await supabase.rpc("my_vehicle_prompts");
  if (error) throw new Error(error.message);
  return (data ?? []) as VehiclePrompt[];
}

/* --------------------------------------------- six-monthly odometer */

export type ReadingStatus = "never" | "due" | "ok" | "overdue";

/**
 * The twice-yearly odometer anchor. The fortnightly check no longer asks
 * for mileage — between anchors the tracker supplies distance covered, so
 * a reading only has to be fresh enough to keep that delta honest.
 */
export interface OdometerStatus {
  vehicle_id: number;
  registration: string;
  colloquial_name: string | null;
  usage_type: "primary_driver" | "pool_van";
  primary_user_id: string | null;
  primary_user_name: string | null;
  /** Nobody is assigned this vehicle at all. */
  driver_unlinked: boolean;
  /** Assigned, but that person has never signed in, so they cannot be
   * prompted yet and the fleet manager holds it. Not a data-entry gap. */
  driver_no_account: boolean;
  primary_person_id: number | null;
  last_reading_miles: number | null;
  last_reading_at: string | null;
  last_reading_source: "driver" | "mot" | "service" | "import" | "correction" | null;
  days_since_reading: number | null;
  reading_status: ReadingStatus;
  estimated_miles: number | null;
  mileage_is_minimum: boolean;
  mileage_gap_days: number | null;
  is_tracked: boolean;
}

export async function fetchOdometerStatus(): Promise<OdometerStatus[]> {
  const { data, error } = await supabase
    .from("v_vehicle_odometer_status")
    .select("*")
    .order("registration");
  if (error) throw error;
  return (data ?? []) as OdometerStatus[];
}

export interface OdometerPrompt {
  vehicle_id: number;
  registration: string;
  colloquial_name: string | null;
  usage_type: "primary_driver" | "pool_van";
  is_mine: boolean;
  reading_status: ReadingStatus;
  last_reading_at: string | null;
  last_reading_miles: number | null;
  days_since_reading: number | null;
  estimated_miles: number | null;
  reason: string;
}

/** Who should be asked for a reading right now — your own vehicle, plus
 * pool vehicles and any vehicle with no linked driver if you run the fleet. */
export async function fetchMyOdometerPrompts(): Promise<OdometerPrompt[]> {
  const { data, error } = await supabase.rpc("my_odometer_prompts");
  if (error) throw new Error(error.message);
  return (data ?? []) as OdometerPrompt[];
}

/** Records a trusted anchor on its own. The RPC rejects a reading that
 * goes backwards or jumps implausibly rather than storing it and leaving
 * someone to notice later. */
export async function recordOdometer(vehicleId: number, miles: number, note?: string): Promise<number> {
  const { data, error } = await supabase.rpc("vehicle_record_odometer", {
    p_vehicle_id: vehicleId,
    p_miles: miles,
    p_note: note ?? null,
  });
  if (error) throw new Error(error.message);
  return data as number;
}

/* ------------------------------------------------- driver assignment */

/**
 * A vehicle belongs to a person on the roster, not to an app account.
 * Whose van it is has nothing to do with whether they have ever signed
 * in — `has_account` only says whether they can be prompted yet.
 */
export interface AssignableDriver {
  person_id: number;
  full_name: string;
  email: string | null;
  has_account: boolean;
}

export async function fetchAssignableDrivers(): Promise<AssignableDriver[]> {
  const { data, error } = await supabase.rpc("assignable_drivers");
  if (error) throw new Error(error.message);
  return (data ?? []) as AssignableDriver[];
}

/** Pass null to unassign. */
export async function setVehicleDriver(vehicleId: number, personId: number | null): Promise<void> {
  const { error } = await supabase.rpc("vehicle_set_driver", {
    p_vehicle_id: vehicleId,
    p_person_id: personId,
  });
  if (error) throw new Error(error.message);
}

/* ------------------------------------------- garage health reports (0240) */

export interface HealthReportRow {
  id: number;
  vehicle_id: number;
  file_id: number | null;
  supplier: string | null;
  job_number: string | null;
  report_date: string | null;
  mileage: number | null;
  technician: string | null;
  served_by: string | null;
  driver_name: string | null;
  vehicle_description: string | null;
  status: "pending" | "confirmed" | "rejected";
  extraction_notes: string | null;
  extracted_at: string;
  confirmed_at: string | null;
  service_record_id: number | null;
}

export interface HealthReportMeasurementRow {
  id: number;
  report_id: number;
  kind: "tyre" | "brake_pad" | "brake_disc";
  position:
    | "nearside_front"
    | "offside_front"
    | "nearside_rear"
    | "offside_rear"
    | "front"
    | "rear";
  mm: number | null;
  percent: number | null;
}

export interface HealthReportItemRow {
  id: number;
  report_id: number;
  urgency: "working_on" | "advisory" | "needs_fixing";
  trade: string | null;
  issue: string;
  recommended_solution: string | null;
  quote: number | null;
  sort_order: number;
}

export async function fetchHealthReports(vehicleId: number): Promise<HealthReportRow[]> {
  const { data, error } = await supabase
    .from("vehicle_health_reports")
    .select(
      "id, vehicle_id, file_id, supplier, job_number, report_date, mileage, technician, served_by, " +
        "driver_name, vehicle_description, status, extraction_notes, extracted_at, confirmed_at, service_record_id",
    )
    .eq("vehicle_id", vehicleId)
    .order("report_date", { ascending: false, nullsFirst: false })
    .order("extracted_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as HealthReportRow[];
}

export async function fetchHealthReportDetail(
  reportId: number,
): Promise<{ measurements: HealthReportMeasurementRow[]; items: HealthReportItemRow[] }> {
  const [m, i] = await Promise.all([
    supabase
      .from("vehicle_health_report_measurements")
      .select("id, report_id, kind, position, mm, percent")
      .eq("report_id", reportId),
    supabase
      .from("vehicle_health_report_items")
      .select("id, report_id, urgency, trade, issue, recommended_solution, quote, sort_order")
      .eq("report_id", reportId)
      .order("sort_order"),
  ]);
  if (m.error) throw m.error;
  if (i.error) throw i.error;
  return {
    measurements: (m.data ?? []) as unknown as HealthReportMeasurementRow[],
    items: (i.data ?? []) as unknown as HealthReportItemRow[],
  };
}

/**
 * Uploads the document, files it against the vehicle, then asks the
 * backend to read it.
 *
 * Takes a LIST because a two-page report photographed on a phone is two
 * files and one report. Read separately they would collide on the job
 * number and each keep only the measurements on its own page.
 *
 * The uploads and the file rows are the caller's own authenticated
 * writes, RLS-governed like every other document in the app. Only the
 * reading is relayed, because that is the half needing the Anthropic key
 * -- which lives in Vault and is never in the browser.
 *
 * Returns once the read has been REQUESTED, not once it has finished: the
 * relay is fire-and-forget through pg_net, so the caller polls
 * fetchHealthReports for the pending row to appear.
 */
export async function uploadAndReadHealthReport(
  vehicleId: number,
  files: File[],
  uploadedBy: string,
): Promise<void> {
  if (files.length === 0) throw new Error("Choose the report, or a photo of the paperwork.");

  const paths: string[] = [];
  let firstFileId: number | null = null;

  for (const file of files) {
    const { path, fileId } = await uploadVehicleDocument(vehicleId, file, uploadedBy, "health_report");
    paths.push(path);
    // The report points at page one; every page is on the Files tab.
    if (firstFileId === null) firstFileId = fileId;
  }

  const { error: syncErr } = await supabase.rpc("request_manual_sync", {
    p_function: "vehicle-health-report-extract",
    p_body: { vehicle_id: vehicleId, storage_paths: paths, file_id: firstFileId },
  });
  if (syncErr) throw syncErr;
}

/**
 * One document against one vehicle: into storage, into `files`, onto the
 * vehicle's Files tab. Returns both the storage path (what the reader
 * needs) and the file id (what a record points at).
 */
export async function uploadVehicleDocument(
  vehicleId: number,
  file: File,
  uploadedBy: string,
  docType: string,
): Promise<{ path: string; fileId: number }> {
  // Shrunk in the browser before it goes anywhere. A phone photo is
  // 3-6MB raw, which wastes an engineer's data in a van and tokens on the
  // way through the reader. DOCUMENT_PROFILE rather than the evidence one
  // on purpose: this is a sheet of paper somebody has to read numbers off,
  // and the evidence size leaves a tyre table's digits marginal. A PDF is
  // passed through untouched.
  const { blob, mimeType } = await compressImage(file, DOCUMENT_PROFILE);

  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const named = mimeType === "image/jpeg" ? safeName.replace(/\.[^.]+$/, "") + ".jpg" : safeName;
  const path = `vehicles/${vehicleId}/${docType}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${named}`;

  const { error: upErr } = await supabase.storage
    .from("documents")
    .upload(path, blob, { contentType: mimeType || "application/octet-stream" });
  if (upErr) throw upErr;

  const { data: fileRow, error: fileErr } = await supabase
    .from("files")
    .insert({
      bucket_id: "documents",
      storage_path: path,
      mime_type: mimeType || "application/octet-stream",
      size_bytes: blob.size,
      uploaded_by: uploadedBy,
    })
    .select("id")
    .single();
  if (fileErr) throw fileErr;

  const { error: linkErr } = await supabase.from("vehicle_files").insert({
    vehicle_id: vehicleId,
    file_id: fileRow.id,
    doc_type: docType,
    description: file.name,
    uploaded_by: uploadedBy,
  });
  if (linkErr) throw linkErr;

  return { path, fileId: fileRow.id as number };
}

/** Turns a pending report into a service record, issues and a mileage
 * reading. Nothing about the vehicle changes until this runs. */
export async function confirmHealthReport(
  reportId: number,
  opts: { createIssues?: boolean; updateMileage?: boolean; advanceSchedule?: boolean } = {},
): Promise<number> {
  const { data, error } = await supabase.rpc("vehicle_health_report_confirm", {
    p_report_id: reportId,
    p_create_issues: opts.createIssues ?? true,
    p_update_mileage: opts.updateMileage ?? true,
    // Off unless asked. An interim check and a full service look much the
    // same on the page, and resetting the clock for an oil change hides a
    // service that is actually due.
    p_advance_schedule: opts.advanceSchedule ?? false,
  });
  if (error) throw error;
  return data as number;
}

export async function rejectHealthReport(reportId: number): Promise<void> {
  const { error } = await supabase.rpc("vehicle_health_report_reject", { p_report_id: reportId });
  if (error) throw error;
}
